import { useCallback, useEffect, useRef, useState } from 'react';
import {
  BrandTitleSection,
  Card,
  Content,
  Desc,
  Logo,
  Overlay,
  ResponsiveBrandTitle,
  Section,
  Square,
  TextBlock,
  Top
} from './styles';
import SVGIcon from '../../../../../assets/images/svg/SVGIcon';
import Button from '../../../../../component/button';
import { getFirebaseUrlFromFile, isEmpty } from '../../../../../helpers/common';
import { getBrandingDetails, updateBrandingDetails } from '../../../../../services/settingServices';
import { BrandDomainDetailsInterface } from '../../../../../interfaces/SettingsInterface';
import { captureException } from '../../../../../services/logService';
import { getBrandDataBasedOnWorkspaceId } from '../../../../../services/appService';
import { Title1 } from '../../../styles';
import { useMobileDevice } from '../../../../../global/useMobile';
import BrandColors from '../../../../../component/brandColors';
import { rootStore } from '../../../../../mobx/rootStore';
import { BUTTON_TYPE, ICON_SIZE } from '../../../../../global/constants';
import { observer } from 'mobx-react-lite';
import Input from '../../../../../component/input';
import Image from '../../../../../component/image';

interface BrandDomainPayload {
  data: {
    primaryColor?: string;
    primaryDarkColor?: string;
    metaTitle?: string;
  };
  logo?: { url: string };
  faviconIcon?: { url: string };
}

function BrandDomain() {
  // States
  const [isLoading, setLoading] = useState(false);
  const [initialLoading, setInitialLoading] = useState(false);
  const [brandDomainDetails, setBrandDomainDetails] = useState<BrandDomainDetailsInterface>({});

  // mobx states
  const { workspaceStore } = rootStore;
  const { currentWorkspace } = workspaceStore || {};

  // Other variables
  const isMobile = useMobileDevice();

  // Loads branding details data for the workspace
  const loadData = useCallback(async () => {
    try {
      setInitialLoading(true);
      if (!isEmpty(currentWorkspace?.id)) {
        const brandingDetails = await getBrandingDetails();
        if (brandingDetails) {
          setBrandDomainDetails(brandingDetails);
        }
      }
    } catch (e) {
      captureException(e);
    } finally {
      setInitialLoading(false);
    }
  }, [currentWorkspace?.id]);

  useEffect(() => {
    loadData();
  }, [loadData]);

  useEffect(() => {
    return () => {
      setBrandDomainDetails({});
    };
  }, []);

  const inputRef = useRef<HTMLInputElement | null>(null);
  const inputLogoRef = useRef<HTMLInputElement | null>(null);

  // Converts file to base64 and uploads to Firebase
  const convertBase64 = (file: File, propsName: string) => {
    return new Promise<void>((resolve, reject) => {
      setInitialLoading(true);

      const fileReader = new FileReader();
      fileReader.readAsDataURL(file);

      fileReader.onload = async () => {
        try {
          const item = {
            profile: fileReader.result,
            file: file
          };
          const brandFileName = propsName === 'logo' ? 'brand-logo' : 'brand-favicon';
          const imageUrl = await getFirebaseUrlFromFile(item, 'branding/', brandFileName);
          setBrandDomainDetails((prev) => ({
            ...prev,
            [propsName]: {
              type: file?.type,
              name: file?.name,
              url: imageUrl
            }
          }));

          resolve();
        } catch (error) {
          reject(error);
        } finally {
          setInitialLoading(false);
        }
      };

      fileReader.onerror = (error) => {
        reject(error);
      };
    });
  };

  // Handles file upload and validation
  const handleFileRead = async (event: React.ChangeEvent<HTMLInputElement>, fieldName: string) => {
    try {
      if (event.target.files) {
        const file = event.target.files?.[0];
        if (file?.size > 2.5e8) {
          rootStore.alertNotificationStore.setErrorMessage('Please upload less than 500kb photo size.');
          return false;
        }
        await convertBase64(file, fieldName);
      }
    } catch (e) {
      captureException(e);
    }
  };

  // Triggers favicon upload click event
  const handleFaviconUploadClick = () => {
    inputRef.current?.click();
  };

  // Triggers logo upload click event
  const handleLogoUploadClick = () => {
    inputLogoRef.current?.click();
  };

  // Updates field values in brand domain details
  const onChangeField = useCallback((propsName: string, value: string) => {
    setBrandDomainDetails((prev) => ({
      ...prev,
      [propsName]: value
    }));
  }, []);

  // Saves branding details to the server
  const onClickSaveDetails = useCallback(async () => {
    try {
      setLoading(true);
      const payload: BrandDomainPayload = {
        data: {
          primaryColor: brandDomainDetails?.primaryColor,
          primaryDarkColor: brandDomainDetails?.primaryDarkColor,
          metaTitle: brandDomainDetails?.metaTitle
        }
      };
      if (brandDomainDetails?.logo?.url?.startsWith('https://firebasestorage')) {
        payload.logo = brandDomainDetails.logo;
      }

      if (brandDomainDetails?.faviconIcon?.url?.startsWith('https://firebasestorage')) {
        payload.faviconIcon = brandDomainDetails?.faviconIcon;
      }
      const response = await updateBrandingDetails(payload);
      if (response) {
        const updatedData = await getBrandingDetails();
        setBrandDomainDetails(updatedData);
        getBrandDataBasedOnWorkspaceId(currentWorkspace?.id || '');
      }
    } catch (e) {
    } finally {
      setLoading(false);
    }
  }, [
    brandDomainDetails?.faviconIcon,
    brandDomainDetails.logo,
    brandDomainDetails?.metaTitle,
    brandDomainDetails?.primaryColor,
    brandDomainDetails?.primaryDarkColor,
    currentWorkspace?.id
  ]);

  return (
    <>
      <Content>
        <Top>
          <Title1>Branding</Title1>
          <Desc>Customize colors and logos to showcase your brand.</Desc>
        </Top>
        <Card>
          <Section>
            {isMobile && <ResponsiveBrandTitle>Brand Colors</ResponsiveBrandTitle>}
            <BrandColors
              lightModeColor={brandDomainDetails?.primaryColor || '#00A16F'}
              darkModeColor={brandDomainDetails?.primaryDarkColor || '#00A16F'}
              onChange={(field: string, hex: string) => onChangeField(field, hex)}
            />
          </Section>
          <Section>
            {isMobile && <ResponsiveBrandTitle>Brand Logo</ResponsiveBrandTitle>}
            <BrandTitleSection>
              <Square>
                <input
                  type='file'
                  onChange={(event) => handleFileRead(event, 'faviconIcon')}
                  ref={inputRef}
                  hidden
                  disabled={isLoading}
                  aria-label='Upload file'
                />
                <Logo role='button' isHover={true} onClick={handleFaviconUploadClick}>
                  <Image
                    imageUrl={
                      !isEmpty(brandDomainDetails?.faviconIcon?.url)
                        ? brandDomainDetails?.faviconIcon?.url
                        : 'empty_favicon'
                    }
                    alt='color'
                    width={isMobile ? 60 : 79}
                    height={isMobile ? 60 : 79}
                    borderRadius='var(--border-radius-4)'
                    className={!isEmpty(brandDomainDetails?.faviconIcon?.url) ? '' : 'empty-logo'}
                  />
                  <Overlay className='upload-block'>
                    <TextBlock>
                      <SVGIcon
                        name='upload-icon'
                        width={ICON_SIZE.LG}
                        height={ICON_SIZE.LG}
                        viewBox={`0 0 ${ICON_SIZE.LG} ${ICON_SIZE.LG}`}
                        fill='var(--text-white)'
                      />
                      {!isMobile && <p>Upload favicon</p>}
                    </TextBlock>
                  </Overlay>
                </Logo>
                <p>Favicon</p>
              </Square>
              <Square>
                <input
                  type='file'
                  onChange={(event) => handleFileRead(event, 'logo')}
                  ref={inputLogoRef}
                  hidden
                  disabled={isLoading}
                  aria-label='Upload file'
                />
                <Logo
                  role='button'
                  className={`logo-img ${isEmpty(brandDomainDetails?.logo?.url) ? 'logo-empty' : ''}`}
                  isHover={true}
                  onClick={handleLogoUploadClick}>
                  <Image
                    imageUrl={
                      !isEmpty(brandDomainDetails?.logo?.url) ? brandDomainDetails?.logo?.url : 'empty_brand_logo'
                    }
                    width={!isEmpty(brandDomainDetails?.logo?.url) ? 160 : 38}
                    height={!isEmpty(brandDomainDetails?.logo?.url) ? 64 : 38}
                    className={!isEmpty(brandDomainDetails?.logo?.url) ? 'logo' : ''}
                  />
                  {isEmpty(brandDomainDetails?.logo?.url) && <h4>Logo Text</h4>}
                  <Overlay className='upload-block' isEmptyLogo={isEmpty(brandDomainDetails?.logo?.url)}>
                    <TextBlock display={true}>
                      <SVGIcon
                        name='upload-icon'
                        width={ICON_SIZE.LG}
                        height={ICON_SIZE.LG}
                        viewBox={`0 0 ${ICON_SIZE.LG} ${ICON_SIZE.LG}`}
                        fill='var(--text-white)'
                      />
                      {!isMobile && <p>Upload logo</p>}
                    </TextBlock>
                  </Overlay>
                </Logo>
                <p>Logo</p>
              </Square>
            </BrandTitleSection>
          </Section>
        </Card>
        <Input
          label='Meta title'
          placeHolder='Enter meta title'
          value={brandDomainDetails?.metaTitle}
          onChange={(e) => onChangeField('metaTitle', e.target.value)}
        />
        <Button
          buttonType='submit'
          title='Save'
          variant={BUTTON_TYPE.PRIMARY}
          isLoading={isLoading}
          onClick={() => onClickSaveDetails()}
          disabled={initialLoading}
        />
      </Content>
    </>
  );
}
export default observer(BrandDomain);
